// Environment sensor client. Govee credentials stay in the Cloudflare Worker.

import { saveSettings } from './storage.js'

function goveeWorkerUrl(path) {
  const base = String(S.settings.cloudUrl || '')
  return base ? base.replace(/\/api\/data(?:\?.*)?$/, path) : ''
}

async function refreshEnvironmentSensor() {
  const selectedSku = String(S.settings.goveeDeviceSku || '')
  const selectedDevice = String(S.settings.goveeDeviceId || '')
  const query = selectedSku || selectedDevice ? '?sku=' + encodeURIComponent(selectedSku) + '&device=' + encodeURIComponent(selectedDevice) : ''
  const url = goveeWorkerUrl('/api/govee/current' + query)
  if (!url || !S.settings.cloudToken) {
    S._environmentSensorStatus = { state:'not_configured', message:'Cloud Sync must be connected first' }
    return S._environmentSensorStatus
  }
  S._environmentSensorStatus = { state:'loading' }
  try {
    const response = await fetch(url, { headers:{ 'X-Token':S.settings.cloudToken } })
    const body = await response.json()
    if (!response.ok || !body.ok) throw new Error(body.error || ('HTTP ' + response.status))
    S._environmentSensorStatus = body.status === 'connected'
      ? { state:'connected', sensor:body.sensor, reading:body.reading, refreshedAt:new Date().toISOString() }
      : { state:body.status || 'sensor_not_found', devices:body.devices || [], message:body.status === 'selection_required' ? 'Choose the freezer sensor' : 'Finish adding the R1 Lite in Govee Home' }
  } catch (error) {
    S._environmentSensorStatus = { state:'error', message:error && error.message ? error.message : 'Sensor could not be reached' }
  }
  if (typeof syncNotificationSnapshotSoon === 'function') syncNotificationSnapshotSoon()
  return S._environmentSensorStatus
}

function renderSensorStatus() {
  if (typeof window !== 'undefined' && typeof window.render === 'function') window.render()
}

function sensorToast(message, type) {
  if (typeof window !== 'undefined' && typeof window.showToast === 'function') window.showToast(message, type)
}

async function refreshEnvironmentSensorUI() {
  S._environmentSensorStatus = { state:'loading', message:'Checking the freezer sensor…' }
  renderSensorStatus()
  const status = await refreshEnvironmentSensor()
  renderSensorStatus()
  if (status.state === 'connected') sensorToast('Freezer sensor updated')
  else if (status.state === 'error' || status.state === 'not_configured') sensorToast(status.message || 'Sensor refresh failed', 'error')
  return status
}

async function selectEnvironmentSensor(sku, device, name) {
  S.settings.goveeDeviceSku = String(sku || '')
  S.settings.goveeDeviceId = String(device || '')
  S.settings.goveeDeviceName = String(name || 'Govee sensor')
  // This is a user preference, not vault data. Persist it through the universal
  // settings path so it survives a PWA close/reopen as well as Electron restarts.
  await saveSettings()
  return refreshEnvironmentSensor()
}

async function discoverEnvironmentSensors() {
  const url = goveeWorkerUrl('/api/govee/devices')
  if (!url || !S.settings.cloudToken) return { ok:false, error:'Cloud Sync must be connected first', devices:[] }
  try {
    const response = await fetch(url, { headers:{ 'X-Token':S.settings.cloudToken } })
    const body = await response.json()
    if (!response.ok || !body.ok) throw new Error(body.error || ('HTTP ' + response.status))
    return { ok:true, devices:body.devices || [] }
  } catch (error) {
    return { ok:false, error:error && error.message ? error.message : 'Sensor discovery failed', devices:[] }
  }
}

async function discoverEnvironmentSensorsUI() {
  S._environmentSensorStatus = { state:'loading', message:'Searching your Govee account…' }
  renderSensorStatus()
  const result = await discoverEnvironmentSensors()
  S._environmentSensorStatus = result.ok
    ? { state:result.devices.length ? 'available' : 'sensor_not_found', devices:result.devices, message:result.devices.length ? result.devices.length + ' compatible sensor(s) found' : 'No compatible temperature sensor found' }
    : { state:'error', message:result.error || 'Sensor discovery failed' }
  renderSensorStatus()
  sensorToast(result.ok ? (result.devices.length ? 'Govee sensor found' : 'No compatible sensor found') : S._environmentSensorStatus.message, result.ok ? undefined : 'error')
  return result
}

async function selectEnvironmentSensorUI(sku, device, name) {
  S._environmentSensorStatus = { state:'loading', message:'Connecting ' + String(name || 'sensor') + '…' }
  renderSensorStatus()
  const status = await selectEnvironmentSensor(sku, device, name)
  renderSensorStatus()
  sensorToast(status.state === 'connected' ? 'Freezer sensor connected' : (status.message || 'Sensor connection failed'), status.state === 'connected' ? undefined : 'error')
  return status
}

export { goveeWorkerUrl, refreshEnvironmentSensor, discoverEnvironmentSensors, selectEnvironmentSensor, refreshEnvironmentSensorUI, discoverEnvironmentSensorsUI, selectEnvironmentSensorUI }
